import { FormatTyp } from 'api/autoExport/autoExport.enum';
import { EventExportFormatSignEnum, EventExportFormatTypeEnum } from 'api/eventsExportFormat/eventsExportFormats.enum';
import { AddEventExportFormatBody } from 'api/eventsExportFormat/eventsExportFormats.types';

import { EventExportFormatParametersValuesEnum } from './addEventExportFormat/AddEventExportFormat.enum';
import { EventsExportFormatSystemRow } from './EventsExportFormats.types';

export const SYSTEM_EVENT_EXPORT_FORMATS: EventsExportFormatSystemRow[] = [
  { kind: 'SYSTEMOWE', id: FormatTyp.TYP1, nazwa: 'TYP1', format: '.fil', formatTyp: FormatTyp.TYP1 },
  { kind: 'SYSTEMOWE', id: FormatTyp.TYP2, nazwa: 'TYP2', format: '.fil', formatTyp: FormatTyp.TYP2 },
  { kind: 'SYSTEMOWE', id: FormatTyp.TYP3, nazwa: 'TYP3', format: '.csv', formatTyp: FormatTyp.TYP3 },
  { kind: 'SYSTEMOWE', id: FormatTyp.TYP4, nazwa: 'TYP4', format: '.csv', formatTyp: FormatTyp.TYP4 },
];

const disabledParameters = {
  numerKarty: { enabled: false, position: 0, dopelnijDo: 1, znak: EventExportFormatSignEnum.ZERO },
  data: { enabled: false, position: 1, format: '' },
  mod: { enabled: false, position: 2 },
  tryb: { enabled: false, position: 3, dopelnijDo: 1 },
  stanWejscia: { enabled: false, position: 4 },
  rcp: { enabled: false, position: 5, rodzaj: EventExportFormatTypeEnum.WSZYSTKIE },
  strefa: { enabled: false, position: 6, dopelnijDo: 1 },
  poszukiwany: { enabled: false, position: 7 },
  rewizja: { enabled: false, position: 8 },
  czytnik: { enabled: false, position: 9, dopelnijDo: 1 },
};

/**
 * Read-only metadata for the predefined system formats. Mirrors the byte-layout
 * encoded procedurally in the backend's `PredefiniowaneFormatyService` so the
 * preview page can render the same parameter shape used by user-defined formats.
 */
export const SYSTEM_EVENT_EXPORT_FORMAT_CONFIGS: Record<FormatTyp, AddEventExportFormatBody> = {
  [FormatTyp.TYP1]: {
    ...disabledParameters,
    nazwa: 'TYP 1',
    separator: '',
    endline: '',
    czytnik: { enabled: true, position: 0, dopelnijDo: 3, znak: EventExportFormatSignEnum.SPACJA },
    numerKarty: { enabled: true, position: 1, dopelnijDo: 8, znak: EventExportFormatSignEnum.SPACJA },
    data: { enabled: true, position: 2, format: 'RRRRMMDDggmmss' },
    mod: { enabled: false, position: 3 },
    tryb: { enabled: false, position: 4, dopelnijDo: 1 },
  },
  [FormatTyp.TYP2]: {
    ...disabledParameters,
    nazwa: 'TYP 2',
    separator: '',
    endline: '',
    czytnik: { enabled: true, position: 0, dopelnijDo: 3, znak: EventExportFormatSignEnum.ZERO },
    numerKarty: { enabled: true, position: 1, dopelnijDo: 8, znak: EventExportFormatSignEnum.ZERO },
    data: { enabled: true, position: 2, format: 'RRRRMMDDggmmss' },
    mod: { enabled: false, position: 3 },
    tryb: { enabled: false, position: 4, dopelnijDo: 1 },
  },
  [FormatTyp.TYP3]: {
    ...disabledParameters,
    nazwa: 'TYP 3',
    separator: '',
    endline: '',
    czytnik: { enabled: true, position: 0, dopelnijDo: 3, znak: EventExportFormatSignEnum.ZERO },
    numerKarty: { enabled: true, position: 1, dopelnijDo: 8, znak: EventExportFormatSignEnum.ZERO },
    data: { enabled: true, position: 2, format: 'RRRRMMDDggmmss' },
    mod: { enabled: false, position: 3 },
    tryb: { enabled: false, position: 4, dopelnijDo: 1 },
  },
  [FormatTyp.TYP4]: {
    ...disabledParameters,
    nazwa: 'TYP 4',
    separator: ';',
    endline: '',
    data: { enabled: true, position: 1, format: 'RRRRMMDD;ggmmss' },
  },
};

export const isSystemFormatTyp = (id: string | undefined): id is FormatTyp =>
  !!id && (Object.values(FormatTyp) as string[]).includes(id);

/**
 * Per-system-format display label overrides. TYP3/TYP4 use `nrEwidencyjny`
 * (cardholder registration number) instead of `numerKarty` per spec — FE schema
 * has only `numerKarty`, so we relabel it for those formats.
 */
export const SYSTEM_FORMAT_PARAMETER_LABEL_OVERRIDES: Record<
  FormatTyp,
  Partial<Record<EventExportFormatParametersValuesEnum, string>>
> = {
  [FormatTyp.TYP1]: {},
  [FormatTyp.TYP2]: {},
  [FormatTyp.TYP3]: {
    [EventExportFormatParametersValuesEnum.NUMER_KARTY]:
      'definitions.events.addExportFormat.parameters.cardHolderRegNumber',
  },
  [FormatTyp.TYP4]: {
    [EventExportFormatParametersValuesEnum.NUMER_KARTY]:
      'definitions.events.addExportFormat.parameters.cardHolderRegNumber',
  },
};

/**
 * Virtual label-only rows appended below the real parameters list. Used to
 * surface aspects of a system format that aren't representable as form fields
 * (e.g. TYP1/TYP2 always emit a constant "9" suffix as "Status uprawnień karty").
 */
export const SYSTEM_FORMAT_EXTRA_DISPLAY_ROWS: Record<FormatTyp, string[]> = {
  [FormatTyp.TYP1]: [
    'definitions.events.addExportFormat.parameters.modAndMode',
    'definitions.events.addExportFormat.parameters.cardPermissionStatus',
  ],
  [FormatTyp.TYP2]: [
    'definitions.events.addExportFormat.parameters.modAndMode',
    'definitions.events.addExportFormat.parameters.cardPermissionStatus',
  ],
  [FormatTyp.TYP3]: ['definitions.events.addExportFormat.parameters.modAndMode'],
  [FormatTyp.TYP4]: ['definitions.events.addExportFormat.parameters.modAndMode'],
};

/**
 * Virtual label-only rows rendered above the real parameters list. Used for
 * TYP4 where the cardholder registration number appears first but has no
 * configurable fields.
 */
export const SYSTEM_FORMAT_TOP_DISPLAY_ROWS: Record<FormatTyp, string[]> = {
  [FormatTyp.TYP1]: [],
  [FormatTyp.TYP2]: [],
  [FormatTyp.TYP3]: [],
  [FormatTyp.TYP4]: ['definitions.events.addExportFormat.parameters.cardHolderRegNumber'],
};
